import api from './api'
import { getJobStatus, type BackgroundJob } from './jobs'
import type {
  MatchEventsResponse,
  PurchasingEnrichResponse,
  PurchasingMeezIngredientsResponse,
  PurchasingMetricsResponse,
  PurchasingPackRulingResponse,
  PurchasingProductMergeResponse,
  PurchasingProductResponse,
  PurchasingProductsResponse,
  PurchasingPurchaseLineReviewResponse,
  PurchasingPurchaseLinePatchRequest,
  PurchasingBulkEditItem,
  PurchasingBulkEditResponse,
  PurchasingMatchBulkRequest,
  PurchasingMatchBulkResponse,
  PurchasingPurchaseLinesResponse,
  PurchasingReviewQueueCountResponse,
  PurchasingReviewQueueResponse,
  PurchasingVendorItemBatchMatchResponse,
  PurchasingVendorItemMatchResponse,
  PurchasingVendorItemPackResponse,
  PurchasingVendorItemRejectResponse,
  PurchasingVendorItemSuggestResponse,
  PurchasingVendorItemUnlinkResponse,
  PurchasingVendorItemsResponse,
  PurchasingVendorProfile,
  PurchasingNewProductInput,
  PurchasingPackValueMutateResponse,
  PurchasingPackValueUsageResponse,
  PurchasingPackValuesResponse,
} from '@/services/purchasingTypes'

export type {
  PurchasingBillExtractionShadowStats,
  PurchasingEnrichResponse,
  PurchasingMeezIngredientRow,
  PurchasingMeezIngredientsResponse,
  PurchasingNewProductInput,
  PurchasingPackRulingResponse,
  PurchasingProductMergeResponse,
  PurchasingProductResponse,
  PurchasingProductRow,
  PurchasingProductsResponse,
  PurchasingPurchaseLineReviewResponse,
  PurchasingPurchaseLineRow,
  PurchasingPurchaseLinesResponse,
  PurchasingReviewQueueCountResponse,
  PurchasingReviewQueueResponse,
  PurchasingReviewQueueRow,
  PurchasingReviewQueueSuggestion,
  PurchasingVendorItemBatchMatchResponse,
  PurchasingVendorItemMatchResponse,
  PurchasingVendorItemPackResponse,
  PurchasingVendorItemRejectResponse,
  PurchasingVendorItemRow,
  PurchasingVendorItemsResponse,
  PurchasingVendorProfile,
  PurchasingVendorProfileEvalRecord,
  PurchasingVendorProfileFlipLogEntry,
  PurchasingVendorProfileNewModeStats,
  MatchEventRow,
  MatchEventsResponse,
  PurchasingMetricsResponse,
  PurchasingMetricsVendorRow,
  PurchasingReviewQueueFlaggedLine,
  PurchasingVendorItemSuggestResponse,
  PurchasingVendorItemUnlinkResponse,
  PurchasingPackValueRow,
  PurchasingPackValueMutateResponse,
  PurchasingPackValueUsageResponse,
  PurchasingPackValuesResponse,
} from '@/services/purchasingTypes'

export type PurchasingReviewQueueParams = {
  status?: 'needs_review' | 'done' | string
  vendor?: string
  cursor?: string
  q?: string
  limit?: number
}

export type PurchasingProductsParams = {
  q?: string
  family?: string
  cogs_account?: string
  status?: string
  cursor?: string
  limit?: number
}

export type PurchasingVendorItemsParams = {
  vendor?: string
  product?: number
  match_status?: string
  q?: string
  cursor?: string
  limit?: number
}

export type PurchasingPurchaseLinesParams = {
  document_id?: number
  product_id?: number
  vendor_item_id?: number
  flagged?: boolean
  cursor?: string
  limit?: number
}

export type PurchasingMeezIngredientsParams = {
  q?: string
  limit?: number
}

export type PurchasingPackValuesParams = {
  q?: string
  limit?: number
}

export type PurchasingVendorItemMatchRequest = {
  product_id?: number
  new_product?: PurchasingNewProductInput
  meez_ingredient_id?: string
  force?: boolean
}

export type PurchasingVendorItemBatchMatchRequest = {
  vendor_item_ids: number[]
  product_id?: number
  new_product?: PurchasingNewProductInput
  meez_ingredient_id?: string
  force?: boolean
}

export type PurchasingVendorItemRejectRequest = {
  product_id?: number
  reason?: string
}

export type PurchasingVendorItemPackRequest = {
  pack_text?: string
  pack_count?: string | number
  unit_size_qty?: string | number
  unit_size_uom?: string
  is_catch_weight?: boolean
  pricing_uom?: string
}

export type PurchasingPackRulingRequest = {
  ruling: 'correction' | 'vendor_change'
  pack_text?: string
  pack_count?: string | number
  unit_size_qty?: string | number
  unit_size_uom?: string
  atomic_uom?: string
  atomic_per_pack?: string | number
  is_catch_weight?: boolean
  pricing_uom?: string
  effective_invoice_date?: string
  raw_printed_pack?: string
}

export type PurchasingProductCreateRequest = PurchasingNewProductInput & {
  meez_ingredient_id?: string
  force?: boolean
}

export type PurchasingProductUpdateRequest = {
  name?: string
  family?: string
  atomic_uom?: string
  brand_policy?: string
  preferred_vendor_item_id?: number
  spec?: Record<string, unknown>
  cogs_account_id?: string
  meez_ingredient_id?: string | null
}

export type PurchasingPurchaseLineReviewRequest = {
  review_status: 'confirmed' | 'excluded' | 'flagged'
  quantity?: string | number
  unit_price?: string | number
  vendor_item_code?: string
  pack_text?: string
  quantity_uom?: string
}

export type PurchasingVendorProfileUpdateRequest = {
  bill_extraction_mode?: 'legacy' | 'shadow' | 'new'
  enabled?: boolean
  notes?: string | null
  force?: boolean
  reason?: string
}

export type PurchasingShadowDisagreementAckRequest = {
  document_ids: number[]
  note?: string
}

export async function getPurchasingReviewQueue(
  params?: PurchasingReviewQueueParams
): Promise<PurchasingReviewQueueResponse> {
  const { data } = await api.get<PurchasingReviewQueueResponse>('/purchasing/review-queue', {
    params,
  })
  return data
}

export async function getPurchasingReviewQueueCount(): Promise<PurchasingReviewQueueCountResponse> {
  const { data } = await api.get<PurchasingReviewQueueCountResponse>(
    '/purchasing/review-queue/count',
  )
  return data
}

export async function listPurchasingProducts(
  params?: PurchasingProductsParams
): Promise<PurchasingProductsResponse> {
  const { data } = await api.get<PurchasingProductsResponse>('/purchasing/products', { params })
  return data
}

export async function listPurchasingVendorItems(
  params?: PurchasingVendorItemsParams
): Promise<PurchasingVendorItemsResponse> {
  const { data } = await api.get<PurchasingVendorItemsResponse>('/purchasing/vendor-items', {
    params,
  })
  return data
}

export async function listPurchasingPurchaseLines(
  params: PurchasingPurchaseLinesParams
): Promise<PurchasingPurchaseLinesResponse> {
  const { data } = await api.get<PurchasingPurchaseLinesResponse>('/purchasing/purchase-lines', {
    params,
  })
  return data
}

export async function searchPurchasingMeezIngredients(
  params?: PurchasingMeezIngredientsParams
): Promise<PurchasingMeezIngredientsResponse> {
  const { data } = await api.get<PurchasingMeezIngredientsResponse>(
    '/purchasing/meez-ingredients',
    { params }
  )
  return data
}

export async function listPurchasingVendorProfiles(): Promise<PurchasingVendorProfile[]> {
  const { data } = await api.get<PurchasingVendorProfile[]>('/purchasing/vendor-profiles')
  return data
}

export async function getPurchasingVendorProfile(
  qboVendorId: string
): Promise<PurchasingVendorProfile> {
  const { data } = await api.get<PurchasingVendorProfile>(
    `/purchasing/vendor-profiles/${encodeURIComponent(qboVendorId)}`
  )
  return data
}

export async function matchPurchasingVendorItem(
  vendorItemId: number,
  payload: PurchasingVendorItemMatchRequest
): Promise<PurchasingVendorItemMatchResponse> {
  const { data } = await api.post<PurchasingVendorItemMatchResponse>(
    `/purchasing/vendor-items/${vendorItemId}/match`,
    payload
  )
  return data
}

export async function matchPurchasingVendorItemsBatch(
  payload: PurchasingVendorItemBatchMatchRequest
): Promise<PurchasingVendorItemBatchMatchResponse> {
  const { data } = await api.post<PurchasingVendorItemBatchMatchResponse>(
    '/purchasing/vendor-items/match-batch',
    payload
  )
  return data
}

export async function rejectPurchasingVendorItem(
  vendorItemId: number,
  payload?: PurchasingVendorItemRejectRequest
): Promise<PurchasingVendorItemRejectResponse> {
  const { data } = await api.post<PurchasingVendorItemRejectResponse>(
    `/purchasing/vendor-items/${vendorItemId}/reject`,
    payload ?? {}
  )
  return data
}

export async function unlinkPurchasingVendorItem(
  vendorItemId: number
): Promise<PurchasingVendorItemUnlinkResponse> {
  const { data } = await api.post<PurchasingVendorItemUnlinkResponse>(
    `/purchasing/vendor-items/${vendorItemId}/unlink`
  )
  return data
}

export async function suggestPurchasingVendorItem(
  vendorItemId: number
): Promise<PurchasingVendorItemSuggestResponse> {
  const { data } = await api.post<PurchasingVendorItemSuggestResponse>(
    `/purchasing/vendor-items/${vendorItemId}/suggest`
  )
  return data
}

export async function getPurchasingMetrics(params?: {
  vendor?: string
}): Promise<PurchasingMetricsResponse> {
  const { data } = await api.get<PurchasingMetricsResponse>('/purchasing/metrics', { params })
  return data
}

export async function listPurchasingMatchEvents(params: {
  vendor_item_id: number
  since?: string
}): Promise<MatchEventsResponse> {
  const { data } = await api.get<MatchEventsResponse>('/purchasing/match-events', { params })
  return data
}

export async function editPurchasingVendorItemPack(
  vendorItemId: number,
  payload: PurchasingVendorItemPackRequest
): Promise<PurchasingVendorItemPackResponse> {
  const { data } = await api.post<PurchasingVendorItemPackResponse>(
    `/purchasing/vendor-items/${vendorItemId}/pack`,
    payload
  )
  return data
}

export async function applyPurchasingVendorItemPackRuling(
  vendorItemId: number,
  payload: PurchasingPackRulingRequest
): Promise<PurchasingPackRulingResponse> {
  const { data } = await api.post<PurchasingPackRulingResponse>(
    `/purchasing/vendor-items/${vendorItemId}/pack-ruling`,
    payload
  )
  return data
}

export async function createPurchasingProduct(
  payload: PurchasingProductCreateRequest
): Promise<PurchasingProductResponse> {
  const { data } = await api.post<PurchasingProductResponse>('/purchasing/products', payload)
  return data
}

export async function updatePurchasingProduct(
  productId: number,
  payload: PurchasingProductUpdateRequest
): Promise<PurchasingProductResponse> {
  const { data } = await api.put<PurchasingProductResponse>(
    `/purchasing/products/${productId}`,
    payload
  )
  return data
}

export async function mergePurchasingProducts(
  sourceId: number,
  targetId: number
): Promise<PurchasingProductMergeResponse> {
  const { data } = await api.post<PurchasingProductMergeResponse>(
    `/purchasing/products/${sourceId}/merge-into/${targetId}`
  )
  return data
}

export async function patchPurchasingPurchaseLine(
  lineId: number,
  payload: PurchasingPurchaseLinePatchRequest
): Promise<PurchasingPurchaseLineReviewResponse> {
  const { data } = await api.patch<PurchasingPurchaseLineReviewResponse>(
    `/purchasing/purchase-lines/${lineId}`,
    payload
  )
  return data
}

export async function patchPurchasingPurchaseLinesBulk(
  edits: PurchasingBulkEditItem[]
): Promise<PurchasingBulkEditResponse> {
  const { data } = await api.patch<PurchasingBulkEditResponse>(
    '/purchasing/purchase-lines/bulk',
    { edits }
  )
  return data
}

export async function matchPurchasingBulk(
  payload: PurchasingMatchBulkRequest
): Promise<PurchasingMatchBulkResponse> {
  const { data } = await api.post<PurchasingMatchBulkResponse>('/purchasing/match/bulk', payload)
  return data
}

export async function updatePurchasingPurchaseLineReview(
  lineId: number,
  payload: PurchasingPurchaseLineReviewRequest
): Promise<PurchasingPurchaseLineReviewResponse> {
  const { data } = await api.post<PurchasingPurchaseLineReviewResponse>(
    `/purchasing/purchase-lines/${lineId}`,
    payload
  )
  return data
}

export async function enrichPurchasingDocument(
  documentId: number,
  options?: { strong?: boolean }
): Promise<PurchasingEnrichResponse> {
  const { data } = await api.post<PurchasingEnrichResponse>(
    `/purchasing/documents/${documentId}/enrich`,
    undefined,
    { params: options?.strong ? { strong: true } : undefined }
  )
  return data
}

export async function enrichPurchasingQboPurchase(
  qboEntityId: string,
  options?: { strong?: boolean }
): Promise<PurchasingEnrichResponse> {
  const { data } = await api.post<PurchasingEnrichResponse>(
    `/purchasing/qbo-purchases/${encodeURIComponent(qboEntityId)}/enrich`,
    undefined,
    { params: options?.strong ? { strong: true } : undefined }
  )
  return data
}

export async function updatePurchasingVendorProfile(
  qboVendorId: string,
  payload: PurchasingVendorProfileUpdateRequest
): Promise<PurchasingVendorProfile> {
  const { data } = await api.put<PurchasingVendorProfile>(
    `/purchasing/vendor-profiles/${encodeURIComponent(qboVendorId)}`,
    payload
  )
  return data
}

export async function ackPurchasingShadowDisagreements(
  qboVendorId: string,
  payload: PurchasingShadowDisagreementAckRequest
): Promise<PurchasingVendorProfile> {
  const { data } = await api.post<PurchasingVendorProfile>(
    `/purchasing/vendor-profiles/${encodeURIComponent(qboVendorId)}/shadow-disagreements/ack`,
    payload
  )
  return data
}

export async function pollEnrichmentJob(jobId: number): Promise<BackgroundJob> {
  return getJobStatus(jobId)
}

export async function listPurchasingPackValues(
  params?: PurchasingPackValuesParams
): Promise<PurchasingPackValuesResponse> {
  const { data } = await api.get<PurchasingPackValuesResponse>('/purchasing/pack-values', {
    params,
  })
  return data
}

export async function getPurchasingPackValueUsage(
  value: string
): Promise<PurchasingPackValueUsageResponse> {
  const { data } = await api.get<PurchasingPackValueUsageResponse>(
    '/purchasing/pack-values/usage',
    { params: { value } }
  )
  return data
}

export async function patchPurchasingPackValue(
  oldValue: string,
  newValue: string
): Promise<PurchasingPackValueMutateResponse> {
  const { data } = await api.patch<PurchasingPackValueMutateResponse>(
    '/purchasing/pack-values',
    { old_value: oldValue, new_value: newValue }
  )
  return data
}

export async function deletePurchasingPackValue(
  value: string,
  replacement?: string | null
): Promise<PurchasingPackValueMutateResponse> {
  const { data } = await api.delete<PurchasingPackValueMutateResponse>(
    '/purchasing/pack-values',
    { data: { value, replacement: replacement ?? null } }
  )
  return data
}

export async function mergePurchasingPackValues(
  values: string[],
  target: string
): Promise<PurchasingPackValueMutateResponse> {
  const { data } = await api.post<PurchasingPackValueMutateResponse>(
    '/purchasing/pack-values/merge',
    { values, target }
  )
  return data
}
